import type {
    DraggedAssets,
    ElementaryService,
    GetAudioResource,
    GetTransportState,
    OpenExternalLink,
    ResourceIDToResource,
    ResourcesService,
    ShowUserNotification,
} from "@/client/core/Core.api";
import {
    AppEnvironment,
    type AppContext,
    type AppDefinition,
    type Core,
    type CoreSignals,
    type CoreState,
    type CoreStreams,
    type DragInfo,
    type MidiCCEvent,
    type MidiNoteEvent,
    type UserNotification,
} from "@/client/core/Core.api";
import {
    webBpmSignal,
    webIsPlayingSignal,
    webTimeSignatureSignal,
} from "@/client/core/audio/host-signals.audio";
import { applyDisplayMode } from "@/client/utils/getDisplayMode";
import { trackEvent } from "@/client/utils/telemetry";
import { initAppAccessGuard } from "@/client/core/services/app-access-guard";
import { initFeatureFlags } from "@/client/core/services/feature-flags.signal";
import { createCoreActions } from "@/client/core/CoreActions";
import { createCoreEventHandlers } from "@/client/core/CoreEventHandlers";
import {
    isLabsAudioResource,
    type LabsMeterEvent,
    type TimeSignature,
} from "@/client/core/platform-types/labs.api";
import { createElementaryServiceForPlugin } from "@/client/core/services/elementary-plugin.service";
import { createElementaryServiceForWebsite } from "@/client/core/services/elementary-website.service";
import { createWebsiteMidiService } from "@/client/core/services/midi-website.service";
import { createPluginCommunication } from "@/client/core/services/plugin-communication.service";
import { createResourcesServiceForPlugin } from "@/client/core/services/resources-plugin.service";
import { createResourcesServiceForWebsite } from "@/client/core/services/resources-website.service";
import { createSidepanelAuthBridge } from "@/client/core/services/sidepanel-auth-bridge";
import { initWebAuthGateIfNeeded } from "@/client/core/services/web-auth.service";
import { installSidepanelHostMarker } from "@/client/utils/sidepanel-host-marker";
import type { CoreStorage } from "@/client/core/services/storage.service";
import type { Shape } from "@/client/utils/geometry";
import { getAppEnvironment, isSidepanelHost } from "@/client/utils/getAppEnvironment";
import { getOperatingSystem } from "@/client/utils/operating-system";
import { createSignal } from "@/client/utils/signal";
import { showErrorFactory } from "@/client/utils/error-handling";
import { createSilentGraph } from "@/client/core/audio/Audio";

declare global {
    interface Window {
        context: AppContext;
    }
}

export interface CreateCoreOptions {
    /**
     * Send a visitor who cannot see this app back to the home page. Off for
     * the home page itself, which everyone can see and which has nowhere
     * further back to send anyone.
     */
    readonly shouldGuardAccess: boolean;
    /**
     * Ask the browser for MIDI access. Off for the home page, where nothing
     * listens for notes and the permission prompt would be the only effect.
     */
    readonly shouldEnableMidi: boolean;
}

const kDefaultCoreOptions: CreateCoreOptions = {
    shouldGuardAccess: true,
    shouldEnableMidi: true,
};

export const createCore = (
    appDefinition: AppDefinition,
    userOptions: Partial<CreateCoreOptions> = {},
): Core => {
    const options: CreateCoreOptions = { ...kDefaultCoreOptions, ...userOptions };
    applyDisplayMode();
    trackEvent("app_opened");
    const environment = getAppEnvironment();
    const operatingSystem = getOperatingSystem();
    const audioContext = new AudioContext();

    const state: CoreState = {
        isTestTonePlaying: false,
        dragInfo: undefined,
        windowShape: { w: window.innerWidth, h: window.innerHeight },
        transport: {
            bpm: 120,
            isPlaying: false,
            timeSignature: { numerator: 4, denominator: 4 },
            position_beats: 0,
        },
        resources: new Map(),
        notifications: [],
    };

    const transportSignals: CoreSignals["transport"] = {
        bpm: createSignal<number>(),
        isPlaying: createSignal<boolean>(),
        timeSignature: createSignal<TimeSignature>(),
        position_beats: createSignal<number>(),
    };

    transportSignals.bpm.listen((bpm) => (state.transport.bpm = bpm));
    transportSignals.isPlaying.listen((isPlaying) => (state.transport.isPlaying = isPlaying));
    transportSignals.timeSignature.listen(
        (timeSignature) => (state.transport.timeSignature = timeSignature),
    );
    transportSignals.position_beats.listen(
        (position_beats) => (state.transport.position_beats = position_beats),
    );

    const signals: CoreSignals = {
        midiNote: createSignal<MidiNoteEvent>(),
        midiCC: createSignal<MidiCCEvent>(),
        audioBuffer: createSignal<AudioBuffer>(),
        assetsDragStarted: createSignal<DraggedAssets>(),
        resources: createSignal<ResourceIDToResource>(),
        notification: createSignal<Readonly<UserNotification>>(),
        windowShape: createSignal<Readonly<Shape>>(),
        dragInfo: createSignal<Readonly<DragInfo> | undefined>(),
        transport: transportSignals,
        meterEvents: createSignal<Readonly<LabsMeterEvent>>(),
    };

    const getAudioResource: GetAudioResource = (id) => {
        const resource = state.resources.get(id.value);
        return resource === undefined || !isLabsAudioResource(resource) ? undefined : resource;
    };
    const showUserNotification: ShowUserNotification = signals.notification.notify;
    let getTransportState: GetTransportState;
    let elementaryService: ElementaryService;
    let resourcesService: ResourcesService;
    let openExternalLink: OpenExternalLink;

    // Before the environment switch, and outside every branch. The function
    // decides for itself whether this is a sidepanel host; putting the call
    // inside a branch is how it ends up somewhere its own guard is already
    // false. An app page needs it as much as the home page does — without it
    // the marker survives home → app and is lost on the way back.
    installSidepanelHostMarker();

    switch (environment) {
        case AppEnvironment.Plugin: {
            const communication = createPluginCommunication(
                appDefinition.directory,
                state,
                signals,
            );
            resourcesService = createResourcesServiceForPlugin(state, communication);
            elementaryService = createElementaryServiceForPlugin(communication.sendElementaryGraph);
            getTransportState = communication.getTransportState;
            openExternalLink = communication.openExternalLink;
            // The plugin host delivers auth tokens over the port (handled in
            // plugin-communication) — audiences resolve from them too.
            initFeatureFlags();
            break;
        }
        case AppEnvironment.Website: {
            if (isSidepanelHost()) {
                // Embedded by a sidepanel host (Sounds plugin Labs tab):
                // tokens and catalog drag hints arrive over the port while
                // everything else runs as the website.
                createSidepanelAuthBridge({
                    onAssetsDragStarted: signals.assetsDragStarted.notify,
                });
            } else {
                // Plain website visit: gate behind Splice login when an Auth0
                // client ID is configured (no-op otherwise/in iframes).
                initWebAuthGateIfNeeded(showErrorFactory(showUserNotification, "Splice login"));
            }
            initFeatureFlags();
            if (options.shouldGuardAccess) {
                initAppAccessGuard(appDefinition);
            }
            const websiteService = createElementaryServiceForWebsite(
                audioContext,
                state,
                signals,
                getAudioResource,
                showUserNotification,
            );
            resourcesService = createResourcesServiceForWebsite(
                appDefinition.directory,
                state,
                signals.resources.notify,
                showUserNotification,
            );
            elementaryService = websiteService;
            getTransportState = () => Promise.resolve(state.transport);
            openExternalLink = (url) => window.open(url, "_blank", "noopener,noreferrer");
            if (options.shouldEnableMidi) {
                createWebsiteMidiService(signals.midiNote, signals.midiCC, signals.notification);
            }

            signals.transport.bpm.notify(webBpmSignal.value);
            signals.transport.isPlaying.notify(webIsPlayingSignal.value);
            signals.transport.timeSignature.notify(webTimeSignatureSignal.value);
            signals.transport.position_beats.notify(0);
            break;
        }
    }

    const streams: CoreStreams = {
        midiNote: signals.midiNote.listen,
        midiCC: signals.midiCC.listen,
        assetsDragStarted: signals.assetsDragStarted.listen,
        resources: signals.resources.listen,
        notification: signals.notification.listen,
        windowShape: signals.windowShape.listen,
        dragInfo: signals.dragInfo.listen,
        transport: {
            bpm: signals.transport.bpm.listen,
            isPlaying: signals.transport.isPlaying.listen,
            timeSignature: signals.transport.timeSignature.listen,
            position_beats: signals.transport.position_beats.listen,
        },
        meterEvents: signals.meterEvents.listen,
    };

    const actions = createCoreActions(
        elementaryService,
        resourcesService,
        streams,
        showUserNotification,
        openExternalLink,
        getTransportState,
    );

    const core: Core = {
        appDefinition,
        environment,
        operatingSystem,
        actions,
        state,
        streams,
    };

    {
        // set up initial state
        createCoreEventHandlers(state, signals);
        actions.setAudioGraph(createSilentGraph());
        void actions.getTransportState();
        actions
            .requestAllResources()
            .catch(showErrorFactory(showUserNotification, "requestAllResources"));
    }

    return core;
};

export const createAppContext = <T_App extends object, T_State = unknown>(
    core: Core,
    app: T_App,
    storage?: CoreStorage<T_State>,
): AppContext<T_App, T_State> => {
    const context: AppContext<T_App, T_State> = { core, app, storage };
    window.context = context as AppContext;
    return context;
};
